import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { captureError } from '../../lib/datahub/errorLogging/captureError';

/** Promoted from ProjectDetail's local class — keeps a section crash from killing the page. */
// Does not self-recover once tripped; parent must remount (e.g. via key) to retry.
export class SectionErrorBoundary extends Component<
  {
    name: string;
    children: ReactNode;
    /**
     * What to render instead of the default red notice.
     *
     * Added for the map surface, where the surrounding chrome already owns a
     * "map unavailable" presentation (MapSkeleton) and a red box inside the map
     * pane would read as a second, different failure. Optional and defaulted,
     * so every existing call site is unchanged.
     */
    fallback?: ReactNode;
  },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // Report DIRECTLY, the way ErrorBoundary and ModuleErrorBoundary already do.
    //
    // Until this call existed, a section crash reached error tracking ONLY as a
    // side effect of the console.error below — through the console interceptor,
    // which reported a re-created Error carrying the INTERCEPTOR's stack. So
    // every section crash in the app arrived with the real throw site gone, no
    // component stack, and grouped with unrelated console output. Reporting here
    // is what makes the console line a dev convenience again rather than the
    // only path to the dashboard.
    //
    // The console.error that follows does NOT double-report: captureError marks
    // this exact error object as delivered, and the interceptor drops a repeat
    // delivery of the same object.
    captureError({
      error,
      capturePoint: 'error_boundary',
      severity: 'critical',
      category: 'react_render',
      componentName: `SectionErrorBoundary(${this.props.name})`,
      additionalContext: {
        sectionName: this.props.name,
        componentStack: info.componentStack,
      },
    });

    console.error(`[SectionErrorBoundary] Section "${this.props.name}" crashed:`, error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700 font-medium">Error loading {this.props.name}</p>
          <p className="text-xs text-red-500 mt-1">Something went wrong loading this section. Try refreshing.</p>
        </div>
      );
    }
    return this.props.children;
  }
}
